/**
 * Der Weg ueber das Kabel: Web Serial.
 *
 * Herausgeloest aus connection.ts, damit derselbe Verbindungsaufbau auch ueber
 * Funk laufen kann. Der Inhalt ist unveraendert uebernommen; die Kommentare
 * halten fest, was dabei am Geraet gelernt wurde.
 */

import { DeviceError } from "@microbit/microbit-connection";
import { fehlerDaten, protokoll } from "./protokoll";
import type { Transport, TransportHooks } from "./transport";

/** Der Roboter meldet sich mit der Kennung von Raspberry Pi. */
export const RASPBERRY_PI_VENDOR_ID = 0x2e8a;

const BAUD_RATE = 115200;

const isRobotPort = (port: SerialPort): boolean =>
  port.getInfo?.().usbVendorId === RASPBERRY_PI_VENDOR_ID;

export class SerialTransport implements Transport {
  readonly kind = "usb" as const;

  private port: SerialPort | undefined;
  private writer: WritableStreamDefaultWriter<Uint8Array> | undefined;
  private streamReader: ReadableStreamDefaultReader<Uint8Array> | undefined;
  private readLoop: Promise<void> | undefined;
  private hooks: TransportHooks | undefined;

  constructor() {
    navigator.serial?.addEventListener("disconnect", this.handleDisconnect);
  }

  isSupported(): boolean {
    return Boolean(navigator.serial);
  }

  async hasAuthorizedDevice(): Promise<boolean> {
    if (!navigator.serial) {
      return false;
    }
    return (await navigator.serial.getPorts()).some(isRobotPort);
  }

  async open(hooks: TransportHooks): Promise<void> {
    if (!navigator.serial) {
      throw new DeviceError({
        code: "unsupported",
        message:
          "Dieser Browser kann keine seriellen Geraete ansprechen. " +
          "Der Lehrroboter braucht Chrome oder Edge.",
      });
    }
    this.hooks = hooks;
    protokoll.ereignis("kabel", "anschluss waehlen");
    const port = await this.choosePort(hooks);
    try {
      await port.open({ baudRate: BAUD_RATE });
    } catch (error) {
      protokoll.ereignis("kabel", "oeffnen fehlgeschlagen", fehlerDaten(error));
      throw error;
    }
    this.port = port;
    this.startReading(port, hooks);
    protokoll.ereignis("kabel", "offen", { info: port.getInfo?.() });
  }

  private async choosePort(hooks: TransportHooks): Promise<SerialPort> {
    const serial = navigator.serial!;
    const known = (await serial.getPorts()).filter(isRobotPort);
    if (known.length > 0) {
      return known[0];
    }
    hooks.beforeRequestDevice();
    try {
      return await serial.requestPort({
        filters: [{ usbVendorId: RASPBERRY_PI_VENDOR_ID }],
      });
    } catch (error) {
      protokoll.ereignis("kabel", "keine auswahl", fehlerDaten(error));
      throw new DeviceError({
        code: "no-device-selected",
        message: "Es wurde kein Board ausgewaehlt.",
        cause: error,
      });
    } finally {
      hooks.afterRequestDevice();
    }
  }

  private startReading(port: SerialPort, hooks: TransportHooks): void {
    const stream = port.readable;
    if (!stream) {
      throw new DeviceError({
        code: "connection-error",
        message: "Der serielle Anschluss liefert keine Daten.",
      });
    }
    this.readLoop = (async () => {
      const streamReader = stream.getReader();
      // Festhalten, damit das Trennen das Lesen abbrechen kann. Ohne das
      // wartet die Schleife auf Daten, die nicht mehr kommen.
      this.streamReader = streamReader;
      try {
        for (;;) {
          const { value, done } = await streamReader.read();
          if (done) {
            break;
          }
          if (value) {
            protokoll.datenverkehr("kabel", "rein", value);
            hooks.onData(value);
          }
        }
        protokoll.ereignis("kabel", "lesen beendet");
      } catch (error) {
        protokoll.ereignis("kabel", "verbindung verloren", fehlerDaten(error));
        hooks.onLost();
      } finally {
        streamReader.releaseLock();
        this.streamReader = undefined;
      }
    })();
  }

  async write(text: string): Promise<void> {
    const writable = this.port?.writable;
    if (!writable) {
      throw new DeviceError({
        code: "not-connected",
        message: "Es besteht keine Verbindung zum Board.",
      });
    }
    if (!this.writer) {
      this.writer = writable.getWriter();
    }
    const bytes = new TextEncoder().encode(text);
    try {
      await this.writer.write(bytes);
    } catch (error) {
      protokoll.ereignis("kabel", "schreiben fehlgeschlagen", {
        bytes: bytes.length,
        ...fehlerDaten(error),
      });
      throw error;
    }
    protokoll.datenverkehr("kabel", "raus", bytes);
  }

  async close(): Promise<void> {
    // Reihenfolge entscheidet. Die Leseschleife wartet auf den naechsten
    // Datensatz und endet erst, wenn der Strom abgebrochen wird. Wer zuerst
    // auf sie wartet, wartet fuer immer: das Trennen kehrte dann nicht
    // zurueck, und der Anschluss blieb belegt.
    await this.streamReader?.cancel().catch(() => undefined);
    await this.readLoop?.catch(() => undefined);
    if (this.writer) {
      await this.writer.close().catch(() => undefined);
      this.writer.releaseLock?.();
      this.writer = undefined;
    }
    // Erst wenn beide Seiten freigegeben sind, laesst sich der Anschluss
    // schliessen.
    await this.port?.close().catch(() => undefined);
    this.readLoop = undefined;
    this.streamReader = undefined;
  }

  async forget(): Promise<void> {
    await this.close();
    if (this.port?.forget) {
      await this.port.forget();
    }
    this.port = undefined;
  }

  dispose(): void {
    navigator.serial?.removeEventListener("disconnect", this.handleDisconnect);
    void this.close();
  }

  private handleDisconnect = (): void => {
    if (this.port) {
      this.hooks?.onLost();
    }
  };
}

export { isRobotPort };
